import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import { announceFirmUpdated } from "@/features/lawFirms/firmEvents";
import { queryClient } from "@/lib/query/queryClient";
import { lawyerSelectors } from "@/redux/lawyer/selectors";

import { customCaseTypesApi } from "./api";
import type { CustomCaseType, CustomCaseTypesResponse } from "./types";

export const customCaseTypesKey = (lawFirmId: string | null) =>
  ["law-firm", lawFirmId ?? "", "custom-case-types"] as const;

const EMPTY: CustomCaseType[] = [];

/** The signed-in lawyer's firm id, from the lawyer record. */
export const useLawyerFirmId = (): string | null => {
  const lawyer = useSelector(lawyerSelectors.selectAll) as {
    law_firm_id?: string | null;
  } | null;
  return lawyer?.law_firm_id || null;
};

/**
 * The firm's custom case types and whether the caller may change them. Every
 * member reads them (the New Case picker); `refresh` after a write, which
 * also tells views reading the firm record (the case workflows) to refetch.
 */
export const useCustomCaseTypes = (lawFirmId: string | null) => {
  const query = useQuery<CustomCaseTypesResponse>(
    {
      queryKey: customCaseTypesKey(lawFirmId),
      queryFn: () => customCaseTypesApi.list(lawFirmId as string),
      enabled: Boolean(lawFirmId),
      staleTime: 60_000
    },
    queryClient
  );
  const refresh = useCallback(async () => {
    if (!lawFirmId) return;
    await queryClient.invalidateQueries({
      queryKey: customCaseTypesKey(lawFirmId)
    });
    announceFirmUpdated(lawFirmId);
  }, [lawFirmId]);
  return {
    types: query.data?.types ?? EMPTY,
    canManage: query.data?.can_manage ?? false,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refresh
  };
};
